import { Box, Checkbox, FormControlLabel, Grid, IconButton, InputAdornment, MenuItem, TextField } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import type React from 'react';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { toast } from 'sonner';
import countries from '../../assets/countries.json';
import BasicButton from '../BasicButton';
import { useTranslation } from 'react-i18next';

export interface CustomerData {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  phone: string;
  country: string;
  email: string;
  password: string;
  locale: string;
  changePasswordOnFirstLogin: boolean;
}

interface CustomerFormProps {
  onValidChange?: (valid: boolean) => void;
}

const CustomerForm = forwardRef<{ getFormattedData: () => void }, CustomerFormProps>(({ onValidChange }, ref) => {
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [showPassword, setShowPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    phone: '',
    country: 'SN',
    email: '',
    password: '',
    changePasswordOnFirstLogin: true,
  });
  
  const {t} = useTranslation();

  // Handle input changes for text fields
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'name') {
      setValidationErrors((prev) => ({
        ...prev,
        name: value.trim() === '' ? 'Name is required' : '',
      }));
    }

    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      let error = '';
      if (!value.trim()) error = 'Email is required';
      else if (!emailRegex.test(value)) error = 'Enter a valid email';
      setValidationErrors((prev) => ({ ...prev, email: error }));
    }
  };

  useEffect(() => {
    if (validationErrors.name || validationErrors.email || !formData?.name || !formData?.email || !formData?.password) {
      onValidChange?.(true);
    } else {
      onValidChange?.(false);
    }
  }, [validationErrors, onValidChange, formData?.name, formData?.email, formData?.password]);

  // Handle checkbox changes for password reset flag
  const handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      changePasswordOnFirstLogin: e.target.checked,
    }));
  };

  // Generate a cryptographically strong password (14 chars, mixed charset).
  const generatePassword = () => {
    const charset =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    const length = 14;
    const randomValues = new Uint32Array(length);
    crypto.getRandomValues(randomValues);
    let pwd = '';
    for (let i = 0; i < length; i++) {
      pwd += charset[randomValues[i] % charset.length];
    }
    setFormData((prev) => ({ ...prev, password: pwd }));
    setPasswordCopied(false);
  };

  const copyPassword = async () => {
    if (!formData.password) return;
    try {
      await navigator.clipboard.writeText(formData.password);
      setPasswordCopied(true);
      toast.success(t('createMerchant.passwordCopied'));
      // Revert the icon after 2s so the user can copy again later.
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch (err) {
      console.error('Clipboard write failed:', err);
      toast.error(t('createMerchant.passwordCopyFailed'));
    }
  };

  useImperativeHandle(ref, () => ({
    getFormattedData: (): CustomerData => {
      const [firstName, ...rest] = formData.name.trim().split(' ');
      const lastName = rest.join(' ');
      return {
        firstName: firstName || '',
        lastName: lastName || '',
        address: formData.address || '',
        city: formData.city || '',
        phone: formData.phone || '',
        country: formData.country || 'SN',
        email: formData.email || '',
        password: formData.password || '',
        locale: navigator.language || 'fr-FR',
        changePasswordOnFirstLogin: formData.changePasswordOnFirstLogin || true,
      };
    },
  }));

  // Render the form fields
  return (
    <Box
      ref={ref}
      component='form'
      sx={{
        '& .MuiTextField-root': { m: 1, width: '100%' },
        mt: 2,
      }}
      noValidate
      autoComplete='off'
    >
      <Grid container spacing={2}>
        {/* Name Input */}
        <Grid size={12}>
          <TextField
            style={{ width: '100%' }}
            name='name'
            label={t("createMerchant.form.representativeName")}
            size='small'
            value={formData.name}
            InputLabelProps={{ shrink: !!formData.name }}
            onChange={handleChange}
            error={!!validationErrors.name}
            helperText={validationErrors.name}
            autoFocus
            autoComplete='off'
          />
        </Grid>

        {/* Address Input */}
        <Grid size={6}>
          <TextField
            label={t("createMerchant.form.companyAddress")}
            name='address'
            size='small'
            value={formData.address}
            onChange={handleChange}
            InputLabelProps={{ shrink: !!formData.address }}
            autoComplete='off'
          />
        </Grid>

        {/* City Input */}
        <Grid size={6}>
          <TextField
            label={t("createMerchant.form.companyCity")}
            size='small'
            name='city'
            value={formData.city}
            onChange={handleChange}
            InputLabelProps={{ shrink: !!formData.city }}
            autoComplete='off'
          />
        </Grid>

        {/* Phone Input */}
        <Grid size={6}>
          <TextField
            label={t("createMerchant.form.companyPhone")}
            size='small'
            type='tel'
            name='phone'
            value={formData.phone}
            onChange={handleChange}
            placeholder='+221743058000'
            inputProps={{
              pattern: '^\\+?[0-9\\s-]{7,15}$',
              title: 'Enter a valid phone number (e.g., +221743058000)',
            }}
            InputLabelProps={{ shrink: !!formData.phone }}
            autoComplete='off'
          />
        </Grid>

        {/* Country Selection */}
        <Grid size={6}>
          <TextField
            select
            label={t("createMerchant.form.companyCountry")}
            size='small'
            name='country'
            value={formData.country}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            autoComplete='off'
          >
            {countries.map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Email Input */}
        <Grid size={12}>
          <TextField
            style={{ width: '100%' }}
            name='email'
            label={t("createMerchant.form.representativeEmail")}
            size='small'
            value={formData.email}
            InputLabelProps={{ shrink: !!formData.email }}
            onChange={handleChange}
            error={!!validationErrors.email}
            helperText={validationErrors.email}
            autoComplete='off'
          />
        </Grid>

        {/* Password Input */}
        <Grid size={6}>
          <TextField
            type={showPassword ? 'text' : 'password'}
            label={t("createMerchant.form.password")}
            name='password'
            size='small'
            value={formData.password}
            onChange={handleChange}
            InputLabelProps={{ shrink: !!formData.password }}
            autoComplete='new-password'
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label={t('createMerchant.copyPassword')}
                    onClick={copyPassword}
                    disabled={!formData.password}
                    edge='end'
                    size='small'
                  >
                    {passwordCopied ? (
                      <CheckIcon fontSize='small' color='success' />
                    ) : (
                      <ContentCopyIcon fontSize='small' />
                    )}
                  </IconButton>
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={() => setShowPassword((v) => !v)}
                    edge='end'
                    size='small'
                  >
                    {showPassword ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Generate Password Button */}
        <Grid size={4} sx={{ display: 'flex', alignItems: 'center' }}>
          <BasicButton
            style={{ margin: '0 20px', padding: '0 20px', height: '40px' }}
            text={t("createMerchant.passwordGenerate")}
            onClickEvent={generatePassword}
          />
        </Grid>

        {/* Password Reset Checkbox */}
        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.changePasswordOnFirstLogin}
                onChange={handleCheckChange}
                name='changePasswordOnFirstLogin'
              />
            }
            label={t("createMerchant.passwordReset")}
          />
        </Grid>
      </Grid>
    </Box>
  );
});

export default CustomerForm;
